import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";

const IMPORT_TYPE = "Category Details";

interface ExportProfile {
  _id: string;
  name: string;
  paymentAccount: string;
  importType: string;
  active: boolean;
}

export function ExportProfilesTab({
  authUserId,
  canManage,
}: {
  authUserId: string | undefined;
  canManage: boolean;
}) {
  const profiles = useQuery(
    api.accountingBillMasters.listExportProfiles,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as ExportProfile[] | undefined;

  const createExportProfile = useMutation(
    api.accountingBillMasters.createExportProfile,
  );
  const updateExportProfile = useMutation(
    api.accountingBillMasters.updateExportProfile,
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<ExportProfile | null>(null);
  const [name, setName] = React.useState("");
  const [paymentAccount, setPaymentAccount] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPaymentAccount("");
    setDialogOpen(true);
  };

  const openEdit = (profile: ExportProfile) => {
    setEditing(profile);
    setName(profile.name);
    setPaymentAccount(profile.paymentAccount);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setIsSaving(true);
    try {
      if (editing) {
        await updateExportProfile({
          authUserId,
          profileId: editing._id,
          name,
          paymentAccount,
        });
        toast.success("Export profile updated");
      } else {
        await createExportProfile({
          authUserId,
          name,
          paymentAccount,
          importType: IMPORT_TYPE,
        });
        toast.success("Export profile created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save export profile",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (profile: ExportProfile) => {
    if (!authUserId) return;
    try {
      await updateExportProfile({
        authUserId,
        profileId: profile._id,
        active: !profile.active,
      });
      toast.success(
        profile.active ? "Profile deactivated" : "Profile activated",
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update profile",
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          Each profile pins the QBO payment account stamped on every exported
          row. The import type is fixed to "{IMPORT_TYPE}".
        </p>
        {canManage && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New profile
          </Button>
        )}
      </div>

      {profiles === undefined ? (
        <Skeleton className="h-32 w-full" />
      ) : profiles.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No export profiles yet. One is required to export bills.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Payment account</TableHead>
                <TableHead>Import type</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="w-32" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((profile) => (
                <TableRow key={profile._id}>
                  <TableCell className="py-1.5 font-medium">
                    {profile.name}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {profile.paymentAccount}
                  </TableCell>
                  <TableCell className="py-1.5">{profile.importType}</TableCell>
                  <TableCell className="py-1.5">
                    <Badge variant={profile.active ? "secondary" : "outline"}>
                      {profile.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Edit export profile"
                          onClick={() => openEdit(profile)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(profile)}
                        >
                          {profile.active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit export profile" : "New export profile"}
            </DialogTitle>
            <DialogDescription>
              The payment account must exactly match a QBO account name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="profile-name">Name</Label>
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Operating checking"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-payment-account">Payment account</Label>
              <Input
                id="profile-payment-account"
                value={paymentAccount}
                onChange={(e) => setPaymentAccount(e.target.value)}
                placeholder="e.g. Chase Checking 1234"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Import type</Label>
              <Input value={IMPORT_TYPE} disabled readOnly />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !name.trim() || !paymentAccount.trim()}
            >
              {isSaving ? "Saving…" : editing ? "Save profile" : "Create profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
